// FAZ-9 / MOD-18 (REQ-51 / K-20) — LDAP/AD ayarları ekranı (security.manage).
//
// SMTP ayar ekranı kopya-deseni: tek-kayıt form + write-only sır + test aksiyonu.
// `bindSecret` write-only: form açıldığında alan HER ZAMAN boş gelir; boş
// gönderilirse BE mevcut sırrı korur. `useSsl` salt-görünüm (her zaman true —
// BIZ-LDAP-02). Grup→rol eşlemesi GroupRoleMapEditor ile; userSearchFilter `{0}`
// yer tutucusunu zorunlu kılar.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { Network, Save, ShieldAlert, ShieldCheck, Wifi } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import i18n from '@/i18n';
import { PageHeader } from '@shared/components/PageHeader';
import { ROUTES } from '@shared/constants/routes';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { NormalizedApiError } from '@api/errors';
import { ErrorCode } from '@shared/constants/errorCodes';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { ldapApi } from '@api/ldap';
import { LDAP_DEFAULT_PORT, LDAP_USER_FILTER_PLACEHOLDER } from '../constants';
import { GroupRoleMapEditor } from '../components/GroupRoleMapEditor';
import type { LdapConfig } from '../types';

const { Text } = Typography;

interface FormValues {
  enabled: boolean;
  host: string;
  port: number;
  bindDn: string;
  bindSecret: string;
  baseDn: string;
  userSearchFilter: string;
  groupBaseDn: string;
  groupRoleMapJson: string;
  trustedCertThumbprint: string;
}

function toFormValues(c: LdapConfig): FormValues {
  return {
    enabled: c.enabled,
    host: c.host,
    port: c.port,
    bindDn: c.bindDn,
    bindSecret: '', // write-only — asla prefill edilmez
    baseDn: c.baseDn,
    userSearchFilter: c.userSearchFilter,
    groupBaseDn: c.groupBaseDn,
    groupRoleMapJson: c.groupRoleMapJson,
    trustedCertThumbprint: c.trustedCertThumbprint,
  };
}

/** Sabit-kategori test hatasını, tanınıyorsa errors namespace'inden yerelleştir. */
function localizeError(code: string | null): string | null {
  if (!code) return null;
  const key = `errors:${code}`;
  // Dinamik anahtar → tipli t()'i baypas et (apiError.ts deseni).
  const translate = i18n.t.bind(i18n) as (k: string) => string;
  return i18n.exists(key) ? translate(key) : code;
}

export function LdapSettingsPage() {
  const { t } = useTranslation(['ldap', 'common']);
  const canManage = useCan(PERMISSIONS.SECURITY_MANAGE);
  const qc = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  const { data, isLoading } = useQuery({
    queryKey: ['ldap-config'],
    queryFn: () => ldapApi.getConfig(),
    enabled: canManage,
  });

  useEffect(() => {
    if (data) form.setFieldsValue(toFormValues(data));
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) =>
      ldapApi.updateConfig({
        host: values.host.trim(),
        port: values.port,
        bindDn: values.bindDn.trim(),
        // Boş → null (BE mevcut sırrı korur).
        bindSecret: values.bindSecret.length > 0 ? values.bindSecret : null,
        baseDn: values.baseDn.trim(),
        userSearchFilter: values.userSearchFilter.trim(),
        groupBaseDn: values.groupBaseDn.trim() ? values.groupBaseDn.trim() : null,
        groupRoleMapJson: values.groupRoleMapJson || '{}',
        trustedCertThumbprint: values.trustedCertThumbprint.trim()
          ? values.trustedCertThumbprint.trim()
          : null,
        enabled: values.enabled,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(['ldap-config'], updated);
      // Sır alanını temizle — düz metni ekranda bırakma.
      form.setFieldValue('bindSecret', '');
      toast.success(t('toast.saved'));
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const testMutation = useMutation({
    mutationFn: () => ldapApi.testConnection(),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(t('test.success'));
        return;
      }
      const localized = localizeError(result.error);
      toast.error(localized ? t('test.failure', { error: localized }) : t('test.failureNoDetail'));
    },
    onError: (err) => {
      if (err instanceof NormalizedApiError && (err.code === ErrorCode.RATE_LIMIT || err.status === 429)) {
        toast.error(t('test.rateLimited'));
        return;
      }
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const breadcrumb = [
    { label: t('common:labels.home'), to: ROUTES.DASHBOARD },
    { label: t('breadcrumb.settings') },
    { label: t('breadcrumb.ldap') },
  ];

  if (!canManage) {
    return (
      <div>
        <PageHeader title={t('page.title')} breadcrumb={breadcrumb} />
        <Card>
          <Alert
            type="error"
            showIcon
            icon={<ShieldAlert size={18} />}
            message={t('noAccess.title')}
            description={t('noAccess.description')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="ldap-settings-page">
      <PageHeader title={t('page.title')} description={t('page.description')} breadcrumb={breadcrumb} />

      <Card
        title={
          <Space>
            <Network size={18} />
            {t('page.title')}
            {data ? (
              <>
                {data.enabled ? (
                  <Tag color="green">{t('status.enabled')}</Tag>
                ) : (
                  <Tag>{t('status.disabled')}</Tag>
                )}
                {data.hasBindSecret ? (
                  <Tag color="blue">{t('status.hasSecret')}</Tag>
                ) : (
                  <Tag color="orange">{t('status.noSecret')}</Tag>
                )}
                <Tag color="cyan" icon={<ShieldCheck size={12} />}>
                  {t('status.ssl')}
                </Tag>
              </>
            ) : null}
          </Space>
        }
        extra={
          data?.updatedAt ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('status.updatedAt', { value: dayjs(data.updatedAt).format('DD.MM.YYYY HH:mm') })}
            </Text>
          ) : null
        }
      >
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : (
          <Form<FormValues>
            form={form}
            layout="vertical"
            initialValues={data ? toFormValues(data) : { port: LDAP_DEFAULT_PORT }}
            onFinish={(values) => saveMutation.mutate(values)}
          >
            <Form.Item
              name="enabled"
              label={t('fields.enabled.label')}
              valuePropName="checked"
              help={t('fields.enabled.help')}
            >
              <Switch />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="host"
                  label={t('fields.host.label')}
                  rules={[{ required: true, message: t('fields.host.required') }]}
                >
                  <Input placeholder={t('fields.host.placeholder')} maxLength={255} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item name="port" label={t('fields.port.label')}>
                  <InputNumber min={1} max={65535} precision={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label={t('fields.useSsl.label')} help={t('fields.useSsl.help')}>
                  {/* Salt-görünüm — BE her zaman true zorlar (BIZ-LDAP-02). */}
                  <Switch checked disabled />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="bindDn" label={t('fields.bindDn.label')}>
                  <Input placeholder={t('fields.bindDn.placeholder')} maxLength={512} autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="bindSecret" label={t('fields.bindSecret.label')} help={t('fields.bindSecret.help')}>
                  <Input.Password
                    placeholder={t('fields.bindSecret.placeholder')}
                    maxLength={255}
                    autoComplete="new-password"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="baseDn" label={t('fields.baseDn.label')}>
              <Input placeholder={t('fields.baseDn.placeholder')} maxLength={512} />
            </Form.Item>

            <Form.Item
              name="userSearchFilter"
              label={t('fields.userSearchFilter.label')}
              help={t('fields.userSearchFilter.help')}
              rules={[
                { required: true, message: t('fields.userSearchFilter.required') },
                {
                  validator: (_, value: string) =>
                    !value || value.includes(LDAP_USER_FILTER_PLACEHOLDER)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('fields.userSearchFilter.placeholderMissing'))),
                },
              ]}
            >
              <Input placeholder={t('fields.userSearchFilter.placeholder')} maxLength={512} />
            </Form.Item>

            <Divider orientation="left" plain>
              {t('groupRoleMap.title')}
            </Divider>

            <Form.Item name="groupBaseDn" label={t('fields.groupBaseDn.label')} help={t('fields.groupBaseDn.help')}>
              <Input placeholder={t('fields.groupBaseDn.placeholder')} maxLength={512} />
            </Form.Item>

            <Form.Item name="groupRoleMapJson" label={t('groupRoleMap.title')}>
              <GroupRoleMapEditor />
            </Form.Item>

            <Form.Item
              name="trustedCertThumbprint"
              label={t('fields.trustedCertThumbprint.label')}
              help={t('fields.trustedCertThumbprint.help')}
            >
              <Input placeholder={t('fields.trustedCertThumbprint.placeholder')} maxLength={128} />
            </Form.Item>

            <Button type="primary" htmlType="submit" icon={<Save size={15} />} loading={saveMutation.isPending}>
              {t('save')}
            </Button>

            <Divider />

            <Text strong>{t('test.title')}</Text>
            <div style={{ marginTop: 4, marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('test.description')}
              </Text>
            </div>
            <Button
              icon={<Wifi size={15} />}
              loading={testMutation.isPending}
              onClick={() => testMutation.mutate()}
            >
              {t('test.button')}
            </Button>
          </Form>
        )}
      </Card>
    </div>
  );
}
